import FullPageLoader from "@src/common/FullPageLoader";
import moment from "moment";
import { useEffect, useMemo, useRef, useState } from "react";
import { NavLink, useNavigate, useSearchParams } from "react-router-dom";
import { isMobile } from "react-device-detect";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { FiCalendar, FiCheck, FiGlobe, FiMapPin, FiNavigation, FiSearch, FiUsers } from "react-icons/fi";
import DynamicFormField, { isShowTravelDynamicField } from "./DynamicFormField";
import { getTravelCommonConfig, getTravelFormConfig } from "@src/store/slices/travel";
import TravellersDetails from "./TravellersDetails";
import PlanSelection from "./PlanSelection";
import type { TravelPlan } from "./PlanSelection";
import Checkout from "./Checkout";
import TravellersDobPopup from "./popups/DobPopup";
import DestinationCountryPopup from "./popups/DestinationCountryPopup";
import { TravelService } from "@src/services/TravelService";
import secureStorage from "@src/config/encrypt";
import { ASIA_REGION_ID, DEFAULT_MAX_TRAVELLER_COUNT, MAX_DESTINATION_COUNTRY_COUNT, MAX_SINGLE_TRIP_DURATION_DAYS, PAGE_TYPE, PAGE_TYPE_STEP_MAP, STATUS_CODES, TRAVEL_CHECKOUT_CONFIG_KEY, TRAVEL_LOCKED_AFTER_LEAD_CREATE_STEPS, TRAVEL_STEPS,PHILIPPINE_COUNTRY_ID, SOURCE } from "@src/config/constant";

const CalendarIcon = FiCalendar as any;
const CheckIcon = FiCheck as any;
const GlobeIcon = FiGlobe as any;
const MapPinIcon = FiMapPin as any;
const NavigationIcon = FiNavigation as any;
const SearchIcon = FiSearch as any;
const UsersIcon = FiUsers as any;

const defaultTravelPlanOptions = [
    { value: "worldwide_schengen", label: "Worldwide/ Schengen" },
];

const getCountryLabel = (country: any) => String(country?.label || "");
const getCountryIcon = (country: any) => String(country?.icon || "").trim();

const CountryAvatarContent = ({ country }: { country: any }) => {
    const label = getCountryLabel(country);
    const icon = getCountryIcon(country);
    const [showIcon, setShowIcon] = useState(Boolean(icon));

    useEffect(() => {
        setShowIcon(Boolean(icon));
    }, [icon]);

    return showIcon ? <img src={icon} alt="" onError={() => setShowIcon(false)} /> : <>{label.slice(0, 2)}</>;
};

const getConfigOptions = (config: any, key: string) => (
    Array.isArray(config?.[key])
        ? config[key].filter((option: any) => option?.value !== undefined && option?.value !== null && option?.label !== undefined && option?.label !== null)
        : []
);

const isAnnualTripOption = (option: any) => {
    const label = String(option?.label || "").toLowerCase();
    return label.includes("annual trip") || label.includes("anual trip");
};

const isSingleTripOption = (option: any) => {
    const label = String(option?.label ?? option?.value ?? option ?? "").toLowerCase();
    return label.includes("single trip");
};

const getTravelRadioOptions = (config: any, key: string) => (
    getConfigOptions(config, key).map((option: any) => (
        isAnnualTripOption(option)
            ? { ...option, disabled: true, coming_soon: true }
            : option
    ))
);

const getTravelInsuranceTypeOptions = (config: any) => (
    getTravelRadioOptions(config, "insurance_type").length
        ? getTravelRadioOptions(config, "insurance_type")
        : getTravelRadioOptions(config, "travel_insurance_type")
);

const isTravellerCountField = (fieldItem: any) => ["no_of_traveller", "no_of_travellers", "traveller_count"].includes(fieldItem?.name);
const isCountryPickerField = (fieldItem: any) => ["countries", "dest_country_id"].includes(fieldItem?.name) || fieldItem?.type === "multiple_front_options_with_sdd";
const isDestinationCountryField = (fieldItem: any) => fieldItem?.name === "destination_country";
const isTravelInsuranceTypeField = (fieldItem: any) => ["travel_insurance_type", "insurance_type"].includes(fieldItem?.name);
const isTravelStartDateField = (fieldItem: any) => ["trip_start_date", "start_date"].includes(fieldItem?.name);
const isTravelEndDateField = (fieldItem: any) => ["trip_end_date", "end_date"].includes(fieldItem?.name);
const isTravelCoverageDetailField = (fieldItem: any) => isTravelInsuranceTypeField(fieldItem) || fieldItem?.name === "trip_type" || isTravellerCountField(fieldItem);

const normalizeTravellerDobValues = (dobValues: any[] = [], count: number) => (
    Array.from({ length: count }, (_item, index) => dobValues[index] || "")
);

const getRawValue = (value: any) => value?.value ?? value ?? "";

const getDateValue = (value: any, format = "YYYY-MM-DD") => (
    value ? moment(value).format(format) : ""
);

const getTripDurationText = (startDate: any, endDate: any) => {
    if (!startDate || !endDate || !moment(startDate).isValid() || !moment(endDate).isValid()) return "";

    const tripDuration = moment(endDate).diff(moment(startDate), "days") + 1;
    if (tripDuration < 1) return "";

    return `${tripDuration} ${tripDuration === 1 ? "Day" : "Days"}`;
};

const getTripDurationDisplayValue = (tripDuration: any) => {
    const durationValue = Number(tripDuration);
    if (!Number.isFinite(durationValue) || durationValue < 1) return "";

    return `${durationValue} ${durationValue === 1 ? "Day" : "Days"}`;
};

const getNumberValue = (value: any, fallback: number | null = null) => {
    const rawValue = getRawValue(value);
    if (rawValue === "" || rawValue === null || rawValue === undefined) return fallback;
    const numberValue = Number(rawValue);
    return Number.isNaN(numberValue) ? fallback : numberValue;
};

const getCountryIdValue = (country: any) => {
    const rawValue = getRawValue(country?.id ?? country?.country_id ?? country?.value);
    return rawValue === "" || rawValue === null || rawValue === undefined ? "" : String(rawValue);
};

const getApiErrorMessage = (error: any, fallback: string) => (
    error?.response?.data?.message
    || error?.response?.data?.error
    || error?.data?.message
    || error?.message
    || fallback
);

const parseCommaSeparatedIds = (value: any) => {
    if (Array.isArray(value)) return value.map((item) => Number(item)).filter((item) => !Number.isNaN(item));
    if (value === null || value === undefined || value === "") return [];

    return String(value)
        .split(",")
        .map((item) => Number(item.trim()))
        .filter((item) => !Number.isNaN(item));
};

const getFieldDateValue = (value: any) => (value ? moment(value).toDate() : "");

const buildInsuredPayload = (dobValues: any[] = [], payload: any = {}) => (
    dobValues
        .map((dobValue: any) => getDateValue(dobValue))
        .map((dob: string, index: number) => {
            if (!dob) return null;

            const existingInsured = payload?.insured?.[index] || {};
            const fallbackFirstName = index === 0 ? String(existingInsured?.first_name || payload?.primary_first_name || "").trim() : "";

            return {
                ...existingInsured,
                dob,
                first_name: String(existingInsured?.first_name || fallbackFirstName).trim(),
            };
        })
        .filter(Boolean)
);

const findOption = (options: any[] = [], value: any) => (
    options.find((option: any) => String(option?.value) === String(value))
);

const getTravelEndDateMinDate = (startDate: any) => (
    startDate ? moment(startDate).startOf("day").toDate() : undefined
);

const getTravelEndDateMaxDate = (startDate: any, isSingleTrip: boolean) => (
    startDate && isSingleTrip ? moment(startDate).add(MAX_SINGLE_TRIP_DURATION_DAYS - 1, "days").endOf("day").toDate() : undefined
);

const sortPrimaryTravellerFirst = (travellerList: any[] = []) => (
    [...travellerList].sort((first: any, second: any) => Number(second?.is_primary === 1) - Number(first?.is_primary === 1))
);

const getPrimaryTravellerName = (leadDetails: any, travellerList: any[] = []) => {
    const leadPrimaryName = String(leadDetails?.primary_first_name || "").trim();
    if (leadPrimaryName) return leadPrimaryName;

    const primaryTraveller = sortPrimaryTravellerFirst(travellerList)[0];
    return String(primaryTraveller?.first_name || "").trim();
};

const passportDocumentIds: Record<string, number> = {
    passport_front: 1,
    passport_back: 2,
};

const getEditSelectedPlan = (leadDetails: any): TravelPlan | null => {
    if (!leadDetails?.plan_id && !leadDetails?.plan_name) return null;

    const planId = leadDetails?.plan_id;

    return {
        id: planId ? `edit-plan-${planId}` : "edit-plan",
        planId,
        insurer: String(leadDetails?.insurer_name || leadDetails?.company_name || ""),
        tier: String(leadDetails?.plan_name || ""),
        price: "",
        logoText: "",
        commission: "",
        benefits: [],
        featureGroups: [],
    };
};

export default function CreateTravelApplication() {

    const [searchParams, setSearchParams] = useSearchParams();
    const [loading, setLoading] = useState(false);
    const [sectionList, setSectionList] = useState<any>([]);
    const [selectedCountries, setSelectedCountries] = useState<any[]>([]);
    const [countrySearch, setCountrySearch] = useState("");
    const [isCountryDropdownOpen, setIsCountryDropdownOpen] = useState(false);
    const [selectedTravelPlans, setSelectedTravelPlans] = useState<any[]>([]);
    const [travellers, setTravellers] = useState<any[]>([]);
    const [editLeadDetails, setEditLeadDetails] = useState<any>(null);
    const [editTravellers, setEditTravellers] = useState<any[]>([]);
    const [travellerDobValues, setTravellerDobValues] = useState<any[]>([]);
    const [isTravellersDobPopupOpen, setIsTravellersDobPopupOpen] = useState(false);
    const [isDestinationPopupOpen, setIsDestinationPopupOpen] = useState(false);
    const [refresh, setRefresh] = useState(false);
    const [isTravelLeadCreated, setIsTravelLeadCreated] = useState(false);
    const countryPanelRef = useRef<HTMLDivElement>(null);
    const formScrollRef = useRef<HTMLDivElement>(null);
    const travellersRef = useRef<any>(null);
    const navigate = useNavigate();
    const [shareLeadId, setShareLeadId] = useState<number | null>(null);
    const dispatch: any = useDispatch();
    const travelState = useSelector((state: any) => state.travel);
    const travelCommonConfig = travelState?.travelCommonConfig;

    const subRoute = searchParams.get("step");
    const editLeadId = Number(searchParams.get("lead_id")) || null;
    const stepList = useMemo(() => TRAVEL_STEPS, []);
    const activeStepIndex = Math.max(stepList.findIndex((step: any) => step.key === subRoute), 0);
    const activeStep = stepList[activeStepIndex];
    const isEditMode = Boolean(editLeadId);
    const activeSectionKey = activeStep?.key === "checkout" ? TRAVEL_CHECKOUT_CONFIG_KEY : activeStep?.key;
    const activeSection = sectionList.find((section: any) => section.key === activeSectionKey);
    const allFields = sectionList?.flatMap((section: any) => section?.blocks?.flatMap((block: any) => block?.fields || []) || []) || [];
    const getField = (name: string) => allFields.find((fieldItem: any) => fieldItem.name === name);
    const getFieldValue = (...names: string[]) => {
        const fieldItem = names.map((name) => getField(name)).find(Boolean);
        return fieldItem?.value;
    };
    const getFieldDisplayValue = (name: string) => {
        const fieldItem = getField(name);
        const value = fieldItem?.value;

        if(name==='dest_country_id'){
            const region_ids = [
            ...new Set(
                value?.map((item: any) => item?.region_id)
            )
           ];

            if(region_ids?.length>1){
                return 'Worldwide/ Schengen'
            }else if(
                region_ids?.length===1 && region_ids?.[0] === ASIA_REGION_ID
            ){
               return 'Asia'
            }else{
                return ""
            }
        }

        const selectedOption = fieldItem?.options?.find(
            (option: any) => String(option?.value) === String(value?.value ?? value)
        );
        if (selectedOption?.label) return selectedOption.label;
        
        if (value instanceof Date) return moment(value).format("DD MMM, YYYY");
        return value?.label ?? value ?? "";
    };
    const getSelectedTravelInsuranceType = (fields: any[] = [], commonConfig: any = travelCommonConfig) => {
        const fieldItem = fields.find((item: any) => isTravelInsuranceTypeField(item));
        const selectedValue = fieldItem?.value?.value ?? fieldItem?.value;
        const options = fieldItem?.options?.length ? fieldItem.options : getTravelInsuranceTypeOptions(commonConfig);

        return options.find((option: any) => String(option?.value) === String(selectedValue)) || fieldItem?.value;
    };
    const travellerField = getField("traveller_count") || getField("no_of_travellers") || getField("no_of_traveller");
    const travellersCount = Number(travellerField?.value?.value ?? travellerField?.value) || 1;
    const maxTravellerCount = Number(travelCommonConfig?.max_traveller_count) || DEFAULT_MAX_TRAVELLER_COUNT;
    const travellerOptions = useMemo(() => Array.from({ length: maxTravellerCount }, (_item, index) => ({
        value: index + 1,
        label: String(index + 1),
    })), [maxTravellerCount]);

    const countryOptions = useMemo(() => {
        const apiCountries = Array.isArray(travelCommonConfig?.travel_countries) ? travelCommonConfig.travel_countries : [];
        return apiCountries.length ? apiCountries : [];
    }, [travelCommonConfig]);

    const homepageCountryOptions = useMemo(() => {
        const hasApiCountries = Array.isArray(travelCommonConfig?.travel_countries) && travelCommonConfig.travel_countries.length;
        if (!hasApiCountries) return countryOptions;

        return [...countryOptions]
            .filter((country: any) => Number(country?.display_on_homepage) === 1)
            .sort((first: any, second: any) => (Number(first?.display_order) || 0) - (Number(second?.display_order) || 0));
    }, [countryOptions, travelCommonConfig]);

    const planOptions = useMemo(() => {
        return defaultTravelPlanOptions;
    }, []);

    const filteredCountryOptions = useMemo(() => {
        const keyword = countrySearch.trim().toLowerCase();
        if (!keyword) return countryOptions;
        return countryOptions.filter((country: any) => getCountryLabel(country).toLowerCase().includes(keyword));
    }, [countryOptions, countrySearch]);

    const getDestinationText = (countries: any[] = selectedCountries) => (
        countries.length
            ? `${countries.slice(0, 3).map((item: any) => getCountryLabel(item)).join(", ")}${countries.length > 3 ? ` +${countries.length - 3} More` : ""}`
            : ""
    );

    const getTravelStepUrl = (stepKey: string) => {
        const params = new URLSearchParams({ step: stepKey });
        if (editLeadId) params.set("lead_id", String(editLeadId));
        return `/create-travel-insurance?${params.toString()}`;
    };

    const getStepPageType = (stepKey: string) => {
        if (stepKey === "trip_details") return PAGE_TYPE.PLAN_SELECTION - 1;
        if (stepKey === "plan_selection") return PAGE_TYPE.PLAN_SELECTION;
        if (stepKey === "traveller_details") return PAGE_TYPE.TRAVELLER_DETAILS;
        if (stepKey === "checkout") return PAGE_TYPE.MAKE_PAYMENT;
        return null;
    };

    const getCountryOptionById = (countryId: any) => countryOptions.find((country: any) => (
        String(country?.value) === String(countryId)
        || String(country?.id) === String(countryId)
        || String(country?.country_id) === String(countryId)
    ));

    const getOriginCountryFromLeadDetails = (leadDetails: any) => {
        const originCountryId = leadDetails?.origin_country_id;
        const countryOption = getCountryOptionById(originCountryId);

        if (countryOption) return countryOption;
        if (leadDetails?.origin_country_name) {
            return {
                value: originCountryId,
                label: leadDetails.origin_country_name,
            };
        }

        return originCountryId;
    };

    const getCountriesFromLeadDetails = (leadDetails: any) => {
        const destinationIds = parseCommaSeparatedIds(leadDetails?.dest_country_id);
        const destinationNames = Array.isArray(leadDetails?.destination_country_name) ? leadDetails.destination_country_name : [];

        return destinationIds.map((id: number, index: number) => {
            const countryOption = getCountryOptionById(id);

            return countryOption || {
                value: id,
                label: destinationNames[index] || String(id),
                id,
            };
        }).filter(Boolean);
    };

    const hydrateTravelSections = (sections: any[] = [], leadDetails: any, countries: any[], travellerList: any[] = []) => {
        const fieldValues: any = {
            insurance_type: leadDetails?.insurance_type,
            travel_insurance_type: leadDetails?.insurance_type,
            trip_type: leadDetails?.trip_type,
            trip_start_date: getFieldDateValue(leadDetails?.trip_start_date),
            start_date: getFieldDateValue(leadDetails?.trip_start_date),
            trip_end_date: getFieldDateValue(leadDetails?.trip_end_date),
            end_date: getFieldDateValue(leadDetails?.trip_end_date),
            trip_duration: getTripDurationText(leadDetails?.trip_start_date, leadDetails?.trip_end_date)
                || getTripDurationDisplayValue(leadDetails?.trip_duration),
            origin_country_id: getOriginCountryFromLeadDetails(leadDetails),
            dest_region_id: parseCommaSeparatedIds(leadDetails?.dest_region_id),
            plan_id: leadDetails?.plan_id,
            primary_first_name: getPrimaryTravellerName(leadDetails, travellerList),
        };
        const travellerCount = Number(leadDetails?.traveller_count) || Math.max(travellerList.length, 1);
        const hasTravelInsuranceType = !!getRawValue(leadDetails?.insurance_type);

        return sections.map((sectionItem: any) => ({
            ...sectionItem,
            blocks: sectionItem?.blocks?.map((blockItem: any) => ({
                ...blockItem,
                fields: blockItem?.fields?.map((fieldItem: any) => {
                    if (isCountryPickerField(fieldItem)) {
                        return { ...fieldItem, value: countries };
                    }

                    if (isDestinationCountryField(fieldItem)) {
                        return { ...fieldItem, value: getDestinationText(countries) };
                    }

                    if (isTravelEndDateField(fieldItem)) {
                        return {
                            ...fieldItem,
                            value: fieldValues[fieldItem.name],
                            is_enable: hasTravelInsuranceType,
                            minDate: getTravelEndDateMinDate(leadDetails?.trip_start_date),
                            maxDate: getTravelEndDateMaxDate(leadDetails?.trip_start_date, isSingleTripOption(getSelectedTravelInsuranceType(blockItem?.fields))),
                        };
                    }

                    if (isTravelStartDateField(fieldItem)) {
                        return { ...fieldItem, value: fieldValues[fieldItem.name], is_enable: hasTravelInsuranceType };
                    }

                    if (isTravellerCountField(fieldItem)) {
                        return {
                            ...fieldItem,
                            value: findOption(fieldItem?.options, travellerCount) || { value: travellerCount, label: String(travellerCount) },
                        };
                    }

                    if (Object.prototype.hasOwnProperty.call(fieldValues, fieldItem.name)) {
                        return { ...fieldItem, value: fieldValues[fieldItem.name] };
                    }

                    return fieldItem;
                }) || [],
            })) || [],
        }));
    };

    const hydrateTravelLeadDetails = (leadDetails: any) => {
        const leadTravellers = sortPrimaryTravellerFirst(
            Array.isArray(leadDetails?.insured) && leadDetails.insured.length
                ? leadDetails.insured
                : leadDetails?.travellers || []
        );
        const countries = getCountriesFromLeadDetails(leadDetails);
        const selectedPlan = getEditSelectedPlan(leadDetails);

        setEditLeadDetails(leadDetails);
        setEditTravellers(leadTravellers);
        setRefresh((current) => !current);
        setTravellerDobValues(normalizeTravellerDobValues(
            leadTravellers.map((traveller: any) => traveller?.dob || traveller?.date_of_birth || ""),
            Number(leadDetails?.traveller_count) || Math.max(leadTravellers.length, 1)
        ));
        setSelectedTravelPlans(selectedPlan ? [selectedPlan] : []);
        if (countries.length) setSelectedCountries(countries);
        setSectionList((currentList: any[]) => hydrateTravelSections(currentList, leadDetails, countries.length ? countries : selectedCountries, leadTravellers));
    };

    const normalizeTravelFormConfig = (formConfig: any[] = [], commonConfig: any = travelCommonConfig) => {
        const configMaxTravellerCount = Number(commonConfig?.max_traveller_count) || DEFAULT_MAX_TRAVELLER_COUNT;
        const configTravellerOptions = Array.from({ length: configMaxTravellerCount }, (_item, index) => ({
            value: index + 1,
            label: String(index + 1),
        }));

        return JSON.parse(JSON.stringify(formConfig || [])).map((sectionItem: any) => {
            const blocks = sectionItem?.blocks || [{
                key: sectionItem.key,
                title: sectionItem.title,
                fields: sectionItem?.fields || [],
            }];

            return {
                ...sectionItem,
                blocks: blocks.map((blockItem: any) => ({
                    ...blockItem,
                    fields: (blockItem?.fields || []).flatMap((fieldItem: any) => {
                        const fieldData = { ...fieldItem };
                        const startDateValue = (blockItem?.fields || []).find((item: any) => isTravelStartDateField(item))?.value;
                        const endDateValue = (blockItem?.fields || []).find((item: any) => isTravelEndDateField(item))?.value;
                        const selectedTravelInsuranceType = getSelectedTravelInsuranceType(blockItem?.fields, commonConfig);
                        const hasTravelInsuranceType = !!getRawValue(selectedTravelInsuranceType);
                        const isSingleTrip = isSingleTripOption(selectedTravelInsuranceType);
                        const hasTripDurationField = (blockItem?.fields || []).some((item: any) => item?.name === "trip_duration");

                        if (isCountryPickerField(fieldData)) {
                            fieldData.is_show = false;
                            fieldData.value = selectedCountries;
                        }

                        if (isDestinationCountryField(fieldData)) {
                            fieldData.type = "ro";
                            fieldData.is_enable = false;
                            fieldData.value = getDestinationText();
                        }

                        if (isTravelStartDateField(fieldData)) {
                            fieldData.is_enable = hasTravelInsuranceType;
                            fieldData.minDate = moment().add(2, "days").startOf("day").toDate();
                        }

                        if (isTravelEndDateField(fieldData)) {
                            fieldData.is_enable = hasTravelInsuranceType;
                            fieldData.minDate = getTravelEndDateMinDate(startDateValue);
                            fieldData.maxDate = getTravelEndDateMaxDate(startDateValue, isSingleTrip);
                        }

                        if (fieldData.name === "origin_country_id") {
                            if (!fieldData.value) fieldData.value = getCountryOptionById(PHILIPPINE_COUNTRY_ID) || PHILIPPINE_COUNTRY_ID;
                        }

                        if (isTravellerCountField(fieldData)) {
                            fieldData.type = "dd";
                            fieldData.options = configTravellerOptions;
                            if (!fieldData.value) fieldData.value = configTravellerOptions[0];
                            if (Number(fieldData.value?.value ?? fieldData.value) > configMaxTravellerCount) {
                                fieldData.value = configTravellerOptions[configTravellerOptions.length - 1] || configTravellerOptions[0];
                            }
                        }
                        
                        if (isTravelInsuranceTypeField(fieldData) || fieldData.name === "trip_type") {
                            fieldData.options = isTravelInsuranceTypeField(fieldData)
                                ? getTravelInsuranceTypeOptions(commonConfig)
                                : getTravelRadioOptions(commonConfig, fieldData.name);
                            const selectedOption = fieldData.options.find((option: any) => String(option?.value) === String(fieldData.value?.value ?? fieldData.value));
                            if (isAnnualTripOption(fieldData.value) || isAnnualTripOption(selectedOption)) {
                                fieldData.value = fieldData.options.find((option: any) => !option.disabled) || "";
                            }
                        }

                        if (isDestinationCountryField(fieldData) && !hasTripDurationField) {
                            return [
                                fieldData,
                                {
                                    id: "trip_duration",
                                    name: "trip_duration",
                                    label: "Trip Duration",
                                    type: "ro",
                                    display_order: (fieldData.display_order || 0) + 1,
                                    keyboard_type: "default",
                                    is_required: false,
                                    is_show: true,
                                    for_web: true,
                                    is_enable: false,
                                    value: getTripDurationText(startDateValue, endDateValue),
                                    validation: {},
                                    parent_depedencies: null,
                                    child_depedencies: null,
                                    error_msg: "",
                                    placeholder: "",
                                    options: [],
                                    field_show: null,
                                    data_type: "string",
                                    is_secondary: fieldData.is_secondary || 0,
                                },
                            ];
                        }

                        return [fieldData];
                    }),
                })),
            };
        });
    };

    const updateSectionOptions = (formConfig: any, commonConfig: any = travelCommonConfig) => {
        const updatedList = normalizeTravelFormConfig(formConfig || [], commonConfig);

        updatedList.forEach((sectionItem: any) => {
            sectionItem?.blocks?.forEach((blockItem: any) => {
                blockItem?.fields?.forEach((fieldItem: any) => {
                    if (fieldItem.name === "travel_plan") {
                        fieldItem.options = defaultTravelPlanOptions;
                        if (!fieldItem.value) {
                            fieldItem.value = defaultTravelPlanOptions[0];
                        }
                    }
                });
            });
        });

        setSectionList(updatedList);
    };

    const updateTripCountryFields = (countries = selectedCountries) => {
        const destinationText = getDestinationText(countries);

        sectionList.forEach((sectionItem: any) => {
            sectionItem?.blocks?.forEach((blockItem: any) => {
                blockItem?.fields?.forEach((fieldItem: any) => {
                    if (isCountryPickerField(fieldItem)) {
                        fieldItem.value = countries;
                    }

                    if (isDestinationCountryField(fieldItem)) {
                        fieldItem.value = destinationText;
                        if (destinationText) fieldItem.error_message = "";
                    }
                });
            });
        });
    };

    const fetchTravelFormConfig = async () => {
        setLoading(true);
        try {
            const [apiConfig, apiCommonConfig]: any = await Promise.all([
                dispatch(getTravelFormConfig()),
                dispatch(getTravelCommonConfig()),
            ]);
            if (apiConfig?.length) {
                updateSectionOptions(apiConfig, apiCommonConfig);
            }
        } finally {
            setLoading(false);
        }
    };

    const validateCurrentSection = () => {
        let isValid = true;

        activeSection?.blocks?.forEach((blockItem: any) => {
            blockItem?.fields?.forEach((fieldItem: any) => {
                const fieldVisible = isShowTravelDynamicField(fieldItem, activeSection?.blocks);
                if (fieldVisible && isTravellerCountField(fieldItem) && normalizeTravellerDobValues(travellerDobValues, travellersCount).some((value) => !value)) {
                    fieldItem.error_message = "Please select traveller date of birth";
                    isValid = false;
                } else if (fieldVisible && fieldItem.is_required && !fieldItem.value) {
                    fieldItem.error_message = fieldItem.error_msg;
                    isValid = false;
                } else {
                    fieldItem.error_message = "";
                }
            });
        });

        if (!isValid) {
            setSectionList([...sectionList]);
            setRefresh(!refresh);
        }

        return isValid;
    };

    const updateTravellerCountField = (count: number) => {
        setSectionList((currentList: any[]) => currentList.map((sectionItem: any) => ({
            ...sectionItem,
            blocks: sectionItem?.blocks?.map((blockItem: any) => ({
                ...blockItem,
                fields: blockItem?.fields?.map((fieldItem: any) => {
                    if (!isTravellerCountField(fieldItem)) return fieldItem;
                    const option = fieldItem?.options?.find((item: any) => Number(item?.value) === count) || { value: count, label: String(count) };
                    return {
                        ...fieldItem,
                        value: option,
                        error_message: "",
                    };
                }) || [],
            })) || [],
        })));
    };

    const handleTravellerDobSave = (count: number, dobValues: any[]) => {
        updateTravellerCountField(count);
        setTravellerDobValues(normalizeTravellerDobValues(dobValues, count));
    };

    const handleSelectFieldClick = (fieldItem: any) => {
        if (!isTravellerCountField(fieldItem) || activeSection?.key !== "trip_details") return;
        setIsTravellersDobPopupOpen(true);
    };

    const handleReadOnlyFieldClick = (fieldItem: any) => {
        if (!isDestinationCountryField(fieldItem) || activeSection?.key !== "trip_details") return;
        setIsDestinationPopupOpen(true);
    };

    const getSelectMenuOpen = (fieldItem: any) => (
        isTravellerCountField(fieldItem) && activeSection?.key === "trip_details" ? false : undefined
    );

    const handleTravelFieldChange = (changedField: any) => {
        if (!isTravelStartDateField(changedField) && !isTravelEndDateField(changedField) && !isTravelInsuranceTypeField(changedField)) return;

        setSectionList((currentList: any[]) => currentList.map((sectionItem: any) => ({
            ...sectionItem,
            blocks: sectionItem?.blocks?.map((blockItem: any) => ({
                ...blockItem,
                fields: (() => {
                    const startDateValue = blockItem?.fields?.find((item: any) => isTravelStartDateField(item))?.value;
                    const endDateValue = blockItem?.fields?.find((item: any) => isTravelEndDateField(item))?.value;
                    const hasTravelInsuranceType = !!getRawValue(getSelectedTravelInsuranceType(blockItem?.fields));
                    const minDate = getTravelEndDateMinDate(startDateValue);
                    const maxDate = getTravelEndDateMaxDate(startDateValue, isSingleTripOption(getSelectedTravelInsuranceType(blockItem?.fields)));
                    const shouldClearEndDate = (isTravelStartDateField(changedField) || isTravelInsuranceTypeField(changedField))
                        && endDateValue
                        && ((minDate && moment(endDateValue).isBefore(moment(minDate), "day"))
                            || (maxDate && moment(endDateValue).isAfter(moment(maxDate), "day")));
                    const tripDurationText = getTripDurationText(startDateValue, shouldClearEndDate ? "" : endDateValue);

                    return blockItem?.fields?.map((fieldItem: any) => {
                        if (isTravelStartDateField(fieldItem)) {
                            return {
                                ...fieldItem,
                                is_enable: hasTravelInsuranceType,
                            };
                        }

                        if (isTravelEndDateField(fieldItem)) {
                            return {
                                ...fieldItem,
                                is_enable: hasTravelInsuranceType,
                                minDate,
                                maxDate,
                                value: shouldClearEndDate ? "" : fieldItem.value,
                            };
                        }

                        if (fieldItem.name === "trip_duration") {
                            return {
                                ...fieldItem,
                                value: tripDurationText,
                            };
                        }

                        return fieldItem;
                    }) || [];
                })(),
            })) || [],
        })));
    };

    const handleCountrySelect = (country: any) => {
        const selectedCountryKeys = selectedCountries.map((item: any) => item.value);
        const isSelected = selectedCountryKeys.includes(country.value);
        if (!isSelected && selectedCountries.length >= MAX_DESTINATION_COUNTRY_COUNT) {
            toast.error(`You can select up to ${MAX_DESTINATION_COUNTRY_COUNT} destination countries.`);
            return;
        }

        const countries = isSelected
            ? selectedCountries.filter((item: any) => item.value !== country.value)
            : [...selectedCountries, country];

        setSelectedCountries(countries);
        updateTripCountryFields(countries);
    };

    const handleDestinationPopupSave = (countries: any[]) => {
        setSelectedCountries(countries);
        updateTripCountryFields(countries);
    };

    const handleTravelPlanToggle = (plan: any) => {        
        setSelectedTravelPlans((currentPlans: any[]) => {
            const isSelected = currentPlans.some((item: any) => item.id === plan.id);
            return isSelected
                ? currentPlans.filter((item: any) => item.id !== plan.id)
                : [...currentPlans, plan];
        });
    };

    const getTravellerFieldValue = (traveller: any, ...names: string[]) => {
        const travellerFields = traveller?.groups?.flatMap((group: any) => group?.fields || []) || [];
        const fieldItem = travellerFields.find((item: any) => names.includes(item?.base_name) || names.includes(item?.name));
        return fieldItem?.value;
    };

    const getSelectedPlanId = () => selectedTravelPlans[selectedTravelPlans.length - 1]?.planId;

    const buildCreateLeadPayload = (travellerList: any[], travellerDocPaths: any[][] = [], pageType?: number) => {
        const { user_data } = secureStorage.getItem("authAgentUserInfo") || {};
        const startDate = getFieldValue("trip_start_date", "start_date");
        const endDate = getFieldValue("trip_end_date", "end_date");
        const formattedStartDate = getDateValue(startDate);
        const formattedEndDate = getDateValue(endDate);
        const tripDuration = formattedStartDate && formattedEndDate
            ? Math.max(moment(formattedEndDate).diff(moment(formattedStartDate), "days") + 1, 1)
            : null;
        const primaryTraveller = travellerList[0] || {};
        const destinationCountryIds = selectedCountries.map(getCountryIdValue).filter(Boolean).join(", ");
        const planIds = selectedTravelPlans
            .map((plan: any) => plan?.planId)
            .map((planId: any) => {
                const numericValue = Number(planId);
                return Number.isFinite(numericValue) ? numericValue : null;
            })
            .filter((planId: any) => planId !== null);

        const payload: any = {
            insurance_type: String(getRawValue(getFieldValue("insurance_type", "travel_insurance_type")) || ""),
            trip_type: String(getRawValue(getFieldValue("trip_type")) || ""),
            traveller_count: travellersCount,
            trip_start_date: formattedStartDate,
            trip_end_date: formattedEndDate,
            source: SOURCE.WEB,
            created_by: user_data?.agency_id || user_data?.id || null,
            created_by_role: user_data?.role_id || 0,
            trip_duration: tripDuration,
            origin_country_id: getNumberValue(getFieldValue("origin_country_id"), PHILIPPINE_COUNTRY_ID),
            dest_country_id: destinationCountryIds,
            dest_region_id: "",
            plan_id: planIds.length <= 1 ? (planIds[0] ?? getNumberValue(getSelectedPlanId())) : planIds,
            insured: travellerList.map((traveller: any, index: number) => {
                const addressTraveller = index > 0 && traveller?.is_same_address_as_primary ? primaryTraveller : traveller;
                const insuredTraveller: any = {
                    is_primary: index === 0 ? 1 : 0,
                    first_name: getRawValue(getTravellerFieldValue(traveller, "first_name")),
                    middle_name: getRawValue(getTravellerFieldValue(traveller, "middle_name")) || "",
                    last_name: getRawValue(getTravellerFieldValue(traveller, "last_name")),
                    suffix: getNumberValue(getTravellerFieldValue(traveller, "suffix")),
                    civil_status: getNumberValue(getTravellerFieldValue(traveller, "civil_status")),
                    gender: getNumberValue(getTravellerFieldValue(traveller, "gender")),
                    dob: getDateValue(getTravellerFieldValue(traveller, "dob", "date_of_birth")),
                    email: getRawValue(getTravellerFieldValue(traveller, "email", "email_address")),
                    mobile: getRawValue(getTravellerFieldValue(traveller, "mobile", "mobile_number")),
                    province: getNumberValue(getTravellerFieldValue(addressTraveller, "province")),
                    city: getNumberValue(getTravellerFieldValue(addressTraveller, "city")),
                    barangay: getNumberValue(getTravellerFieldValue(addressTraveller, "barangay", "district")),
                    address: getRawValue(getTravellerFieldValue(addressTraveller, "address", "street_address")),
                    id_type: getRawValue(getTravellerFieldValue(traveller, "id_type")),
                    id_no: getRawValue(getTravellerFieldValue(traveller, "id_no")),
                    nationality: getRawValue(getTravellerFieldValue(traveller, "nationality")),
                    passport_no: getRawValue(getTravellerFieldValue(traveller, "passport_no")),
                    passport_expiry: getDateValue(getTravellerFieldValue(traveller, "passport_expiry", "passport_expiry_date")),
                };

                if (travellerDocPaths[index]?.length) {
                    insuredTraveller.doc_path = travellerDocPaths[index];
                }

                if (editLeadId && traveller?.person_id) {
                    insuredTraveller.person_id = traveller.person_id;
                }

                if (index > 0) {
                    insuredTraveller.relationship_with = getNumberValue(getTravellerFieldValue(traveller, "relationship_with"));
                }

                if (index === 0) {
                    insuredTraveller.emer_suffix = getNumberValue(getTravellerFieldValue(traveller, "emer_suffix"));
                    insuredTraveller.emer_first_name = getRawValue(getTravellerFieldValue(traveller, "emer_first_name"));
                    insuredTraveller.emer_middle_name = getRawValue(getTravellerFieldValue(traveller, "emer_middle_name")) || "";
                    insuredTraveller.emer_last_name = getRawValue(getTravellerFieldValue(traveller, "emer_last_name"));
                    insuredTraveller.emer_number = getRawValue(getTravellerFieldValue(traveller, "emer_number", "emer_mobile", "emergency_number"));
                    insuredTraveller.emer_relationship = getRawValue(getTravellerFieldValue(traveller, "emer_relationship"));
                    insuredTraveller.emer_travel_purpose = getNumberValue(getTravellerFieldValue(traveller, "emer_travel_purpose", "travel_purpose"));
                }

                return insuredTraveller;
            }),
            primary_first_name: String(getRawValue(getFieldValue("primary_first_name")) || ""),
        };

        if (shareLeadId) payload.lead_id = shareLeadId;        
        if (editLeadId) {
            payload.lead_id = editLeadDetails?.leadId || editLeadDetails?.lead_id || editLeadId;
        }
        if (pageType) payload.page_type = pageType;

        return payload;
    };

    const uploadPassportDocuments = async (travellerList: any[]) => (
        Promise.all(travellerList.map(async (traveller: any) => {
            const docPath: any[] = [];
            const documents = traveller?.documents || {};

            for (const key of Object.keys(passportDocumentIds)) {
                const file = documents[key];
                if (!(file instanceof File)) continue;

                const response: any = await TravelService.uploadTravelLeadDocument({
                    lead_id: "insurance_id_orcr",
                    images: file,
                });
                const uploadedDocument = response?.data?.data?.documents?.[0];
                if (!uploadedDocument?.file_name) {
                    throw response;
                }

                docPath.push({
                    document_id: passportDocumentIds[key],
                    url: uploadedDocument.file_name,
                });
            }

            return docPath;
        }))
    );

    const fetchAndHydrateTravelLeadDetails = async (leadId: number, showLoader = true) => {
        if (showLoader) setLoading(true);
        try {
            const response: any = await TravelService.travelLeadDetails({ lead_id: leadId });
            const responseData = response?.data;
            const leadDetails = responseData?.data;
            const isSuccessResponse = responseData?.status === STATUS_CODES.success || response?.status === STATUS_CODES.success;

            if (isSuccessResponse && leadDetails) {
                hydrateTravelLeadDetails(leadDetails);
                const editStep = PAGE_TYPE_STEP_MAP[Number(leadDetails?.page_type)];
                if (editStep && subRoute !== editStep) {
                    navigate(getTravelStepUrl(editStep), { replace: true });
                }
                return true;
            }

            toast.error(responseData?.message || "Unable to fetch travel lead details");
            return false;
        } catch (error: any) {
            toast.error(getApiErrorMessage(error, "Unable to fetch travel lead details"));
            return false;
        } finally {
            if (showLoader) setLoading(false);
        }
    };

    const submitCreateLead = async (travellerList: any[]) => {
        setLoading(true);
        const fallbackMessage = editLeadId ? "Unable to update travel lead" : "Unable to create travel lead";
        try {
            const travellerDocPaths = await uploadPassportDocuments(travellerList);
            const payload = buildCreateLeadPayload(travellerList, travellerDocPaths, PAGE_TYPE.MAKE_PAYMENT)
            delete payload?.primary_first_name;
            const response: any = await TravelService.createTravelLead(payload);
            if (response?.data?.status === STATUS_CODES["success"]) {
                if (editLeadId) {
                    await fetchAndHydrateTravelLeadDetails(editLeadId, false);
                }
                setShareLeadId(response?.data?.data?.id);
                toast.success(response?.data?.message || (editLeadId ? "Travel lead updated successfully" : "Travel lead created successfully"));
                return true;
            }

            toast.error(getApiErrorMessage(response, fallbackMessage));
            return false;
        } catch (error: any) {
            toast.error(getApiErrorMessage(error, fallbackMessage));
            return false;
        } finally {
            setLoading(false);
        }
    };

    const submitPlanSelectionLead = async () => {
        setLoading(true);
        try {
            const payload = buildCreateLeadPayload(Array.isArray(travellers) ? travellers : []);
            const selectedNumericPlanIds = selectedTravelPlans
                .map((plan: any) => plan?.planId ?? String(plan?.id || "").replace("edit-plan-", ""))
                .map((planId: any) => {
                    const numericValue = Number(planId);
                    return Number.isFinite(numericValue) ? numericValue : null;
                })
                .filter((planId: any) => planId !== null);
            const insured = buildInsuredPayload(travellerDobValues, payload);

            payload.page_type = PAGE_TYPE.TRAVELLER_DETAILS;
            if (selectedNumericPlanIds.length) {
                payload.plan_id = selectedNumericPlanIds[selectedNumericPlanIds.length - 1];
                payload.shared_plan_ids = selectedNumericPlanIds.map(String).join(",");
            }
            payload.insured = insured;
            delete payload?.primary_first_name;

            const response: any = await TravelService.createTravelLead(payload);
            const responseData = response?.data;
            const createdLeadId = responseData?.data?.id;
            const isSuccessResponse = responseData?.status === STATUS_CODES.success || response?.status === STATUS_CODES.success || createdLeadId;

            if (isSuccessResponse) {
                if (createdLeadId) setShareLeadId(createdLeadId);
                return true;
            }

            toast.error(getApiErrorMessage(response, "Unable to create travel lead"));
            return false;
        } catch (error: any) {
            toast.error(getApiErrorMessage(error, "Unable to create travel lead"));
            return false;
        } finally {
            setLoading(false);
        }
    };

    const handleContinue = async () => {
        if (activeSection?.key === "traveller_details") {
            if (!travellersRef.current?.validate()) return;
            const currentTravellers = travellersRef.current?.getTravellers?.() || travellers;
            setTravellers(currentTravellers);
            const isLeadCreated = await submitCreateLead(currentTravellers);
            if (!isLeadCreated) return;
            setIsTravelLeadCreated(true);
        } else if (!validateCurrentSection()) return;

        if (activeSection?.key === "plan_selection") {
            const isLeadCreated = await submitPlanSelectionLead();
            if (!isLeadCreated) return;
        }

        if (activeSection?.key === TRAVEL_CHECKOUT_CONFIG_KEY) {
            navigate("/dashboard");
            return;
        }

        const nextStep = stepList[activeStepIndex + 1];
        if (nextStep) {
            navigate(getTravelStepUrl(nextStep.key));
        }
    };

    const renderSummaryBar = () => {
        if (!["plan_selection", "traveller_details", TRAVEL_CHECKOUT_CONFIG_KEY].includes(activeSection?.key)) return null;

        const startDate = getFieldDisplayValue("start_date") || getFieldDisplayValue("trip_start_date");
        const endDate = getFieldDisplayValue("end_date") || getFieldDisplayValue("trip_end_date");
        const destination = getCountryLabel(selectedCountries[0]) || "-";
        const extraDestinationCount = selectedCountries.length > 1 ? selectedCountries.length - 1 : 0;
        const destinationTitle = selectedCountries.map((country: any) => getCountryLabel(country)).filter(Boolean).join(", ");
        const tripType = getFieldDisplayValue("trip_type") || "-";

        return (
            <div className="travel-summary-bar">
                <div className="travel-summary-content">
                    <span className="f-13 travel-summary-item">
                        <MapPinIcon />
                        <span className="travel-summary-destination-text">
                            {destination}
                            {extraDestinationCount ? (
                                <>
                                    , <button className="travel-more-destinations" type="button" title={destinationTitle}>+{extraDestinationCount} More</button>
                                </>
                            ) : null}
                        </span>
                    </span>
                    <span className="f-13 travel-summary-item travel-summary-plan"><GlobeIcon /> {getFieldDisplayValue("dest_country_id") || "Worldwide/ Schengen"}</span>
                    <span className="f-13 travel-summary-item"><CalendarIcon /> {startDate && endDate ? `${startDate} - ${endDate}` : "-"}</span>
                    <span className="f-13 travel-summary-item"><NavigationIcon /> {tripType}</span>
                    <span className="f-13 travel-summary-item"><UsersIcon /> {travellersCount} {travellersCount > 1 ? "Travellers" : "Traveller"}</span>
                </div>
                {(isEditMode || activeSection?.key !== "traveller_details") ? (
                    <button type="button" className="f-13" disabled={!isEditMode && isTravelLeadCreated} onClick={() => navigate(getTravelStepUrl("trip_details"))}>Edit</button>
                ) : null}
            </div>
        );
    };

    const renderSectionHeading = (title: string, isTripDetails = false) => {
        if (!title) return null;

        return (
            <div className="col-md-12 col-xs-12">
                <div className="summery-heading">
                    <h2 className={`heading-h2${isTripDetails ? " travel-trip-heading" : ""}`}>{title}</h2>
                </div>
            </div>
        );
    };

    useEffect(() => {
        fetchTravelFormConfig();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!editLeadId || !sectionList.length || Number(editLeadDetails?.leadId || editLeadDetails?.lead_id) === editLeadId) return;

        fetchAndHydrateTravelLeadDetails(editLeadId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editLeadId, sectionList.length, countryOptions]);

    useEffect(() => {
        if (!subRoute) {
            const params: any = { step: stepList[0]?.key || "trip_details" };
            if (editLeadId) params.lead_id = String(editLeadId);
            setSearchParams(params);
        }
    }, [editLeadId, subRoute, setSearchParams, stepList]);

    useEffect(() => {
        if (!isEditMode && isTravelLeadCreated && activeStep?.key && TRAVEL_LOCKED_AFTER_LEAD_CREATE_STEPS.includes(activeStep.key)) {
            navigate(getTravelStepUrl("checkout"), { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeStep?.key, isEditMode, isTravelLeadCreated]);

    useEffect(() => {
        if (activeSection?.key !== "traveller_details") return;

        const frameId = window.requestAnimationFrame(() => {
            if (formScrollRef.current) {
                formScrollRef.current.scrollTop = 0;
                formScrollRef.current.scrollLeft = 0;
            }
            window.scrollTo(0, 0);
        });

        return () => window.cancelAnimationFrame(frameId);
    }, [activeSection?.key]);

    useEffect(() => {
        if (sectionList?.length) {
            updateSectionOptions(sectionList);
        }
        // eslint-disable-next-line
    }, [countryOptions, planOptions, travellerOptions]);

    useEffect(() => {
        const selectedCountryList = selectedCountries
            .map((selectedCountry: any) => countryOptions.find((country: any) => (
                country.value === selectedCountry.value || getCountryLabel(country) === getCountryLabel(selectedCountry)
            )))
            .filter(Boolean);

        if (selectedCountryList.length) {
            setSelectedCountries(selectedCountryList);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [countryOptions]);

    useEffect(() => {
        updateTripCountryFields();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sectionList.length, selectedCountries]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (countryPanelRef.current && !countryPanelRef.current.contains(event.target as Node)) {
                setIsCountryDropdownOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const primaryName = buildCreateLeadPayload(travellers, [], PAGE_TYPE.MAKE_PAYMENT)?.primary_first_name;
    

    return (
        <>
            <FullPageLoader show={loading} />
            <div className="container-fluid add-insurnce-outer bg-light travel-create-page">
                <div className="top-heading financer-lead-heding">
                    <h1>Add Travel Insurance Application</h1>
                </div>

                <div className="lead-detail-tabs">
                    <div className="detail-form-sec">
                        <div className="menu-tab-left-panel">
                            <div className="dealer-tab-left-panel nav">
                                <ul>
                                    {stepList.map((step: any, index: number) => {
                                        const isLocked = !isEditMode && isTravelLeadCreated && TRAVEL_LOCKED_AFTER_LEAD_CREATE_STEPS.includes(step.key);
                                        const isAccessible = !isLocked && (isEditMode || index <= activeStepIndex);
                                        const stepPageType = getStepPageType(step.key);
                                        const leadPageType = Number(editLeadDetails?.page_type);
                                        const activeStepPageType = getStepPageType(activeStep?.key);
                                        const effectiveLeadPageType = Math.max(leadPageType || 0, activeStepPageType || 0);
                                        const isCompleted = isEditMode && effectiveLeadPageType && stepPageType
                                            ? stepPageType < effectiveLeadPageType
                                            : index < activeStepIndex;
                                        const className = isCompleted ? "completed" : "";
                                        return (
                                            <li key={step.key}>
                                                {isAccessible ? (
                                                    <NavLink to={getTravelStepUrl(step.key)} className={className}>
                                                        <span className="img-type">{isMobile && index + 1}</span>
                                                        {step.title}
                                                    </NavLink>
                                                ) : (
                                                    <a href="#!" className={className} onClick={(event) => event.preventDefault()}>
                                                        <span className="img-type">{isMobile && index + 1}</span>
                                                        {step.title}
                                                    </a>
                                                )}
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                        </div>

                        <div className="route-files">
                            <div className="insurance-create-outer relative" style={{ display: "block" }}>
                                <div className="loan-expect-left-panle">
                                    <div className="loan-form">
                                        <div className="loan-form-filed-scroll" ref={formScrollRef}>
                                            <div className="loan-filed-sec">
                                                {renderSummaryBar()}
                                                <div className="form-group-bx vehchile-detail-form">
                                                    {activeSection?.key === "plan_selection" ? (
                                                        <PlanSelection
                                                            selectedPlanIds={selectedTravelPlans.map((plan: any) => plan.id)}
                                                            onTogglePlan={handleTravelPlanToggle}
                                                            travellerList={travellers}
                                                            travellerDobValues={travellerDobValues}
                                                            buildCreateLeadPayload={buildCreateLeadPayload}
                                                            setLoading={setLoading}
                                                            leadId={shareLeadId}
                                                            setLeadId={setShareLeadId}
                                                        />
                                                    ) : activeSection?.key === "traveller_details" ? (
                                                        <TravellersDetails
                                                            ref={travellersRef}
                                                            travellersCount={travellersCount}
                                                            travellerDobValues={travellerDobValues}
                                                            formSection={activeSection}
                                                            travelCommonConfig={travelCommonConfig}
                                                            prefilledTravellers={editTravellers}
                                                            prefillKey={`${editLeadDetails?.leadId || editLeadId}-${refresh}`}
                                                            onTravellersChange={setTravellers}
                                                            primaryName={primaryName}
                                                        />
                                                    ) : activeSection?.key === TRAVEL_CHECKOUT_CONFIG_KEY ? (
                                                        <Checkout
                                                            selectedPlanIds={selectedTravelPlans.map((plan: any) => plan.id)}
                                                            selectedPlans={selectedTravelPlans}
                                                            travellerList={travellers}
                                                            travellerDobValues={travellerDobValues}
                                                            buildCreateLeadPayload={buildCreateLeadPayload}
                                                            leadId={shareLeadId || editLeadDetails?.leadId || editLeadDetails?.lead_id || editLeadId}
                                                            setLoading={setLoading}
                                                        />
                                                    ) : (
                                                        <div className={`row${activeSection?.key === "trip_details" ? " travel-trip-details-form" : ""}`}>
                                                            {activeSection?.blocks?.map((blockItem: any, index: number) => {
                                                                const isTripDetailsSection = activeSection.key === "trip_details";
                                                                const tripDetailsTitle = blockItem.title || activeSection.title;

                                                                return (
                                                                    <div className={`row w-100${isTripDetailsSection ? " travel-trip-details-block" : ""}`} key={`${activeSection.key}-${index}`}>
                                                                        {!isTripDetailsSection && renderSectionHeading(blockItem.title)}
                                                                        {isTripDetailsSection && (
                                                                            <div className="col-md-12">
                                                                                <div className="travel-country-panel" ref={countryPanelRef}>
                                                                                    <h3 className="f-16">Select Countries</h3>
                                                                                    <div className="search-bx travel-country-search">
                                                                                        <SearchIcon />
                                                                                        <input
                                                                                            type="text"
                                                                                            placeholder="Select one or more countries (e.g. France, Italy...)"
                                                                                            value={isCountryDropdownOpen ? countrySearch : selectedCountries.map((item: any) => getCountryLabel(item)).join(", ")}
                                                                                            onChange={(event) => setCountrySearch(event.target.value)}
                                                                                            onFocus={() => setIsCountryDropdownOpen(true)}
                                                                                            onClick={() => setIsCountryDropdownOpen(true)}
                                                                                        />
                                                                                        {isCountryDropdownOpen && (
                                                                                            <button
                                                                                                type="button"
                                                                                                className="travel-country-search-close"
                                                                                                aria-label="Close country dropdown"
                                                                                                onClick={() => setIsCountryDropdownOpen(false)}
                                                                                            >
                                                                                                <i className="ic-clearclose"></i>
                                                                                            </button>
                                                                                        )}
                                                                                        {isCountryDropdownOpen && (
                                                                                            <div className="travel-country-dropdown">
                                                                                                {filteredCountryOptions.map((country: any) => {
                                                                                                    const active = selectedCountries.some((item: any) => item.value === country.value);
                                                                                                    return (
                                                                                                        <button
                                                                                                            type="button"
                                                                                                            className={active ? "active" : ""}
                                                                                                            key={`dropdown-${country.value}`}
                                                                                                            onClick={() => handleCountrySelect(country)}
                                                                                                        >
                                                                                                            <span>{getCountryLabel(country)}</span>
                                                                                                            {active && <CheckIcon />}
                                                                                                        </button>
                                                                                                    );
                                                                                                })}
                                                                                            </div>
                                                                                        )}
                                                                                    </div>

                                                                                    <ul className="travel-country-list">
                                                                                        {homepageCountryOptions.map((country: any, countryIndex: number) => {
                                                                                            const active = selectedCountries.some((item: any) => item.value === country.value);
                                                                                            return (
                                                                                                <li key={`${country.value}-${countryIndex}`}>
                                                                                                    <button
                                                                                                        type="button"
                                                                                                        className={active ? "active" : ""}
                                                                                                        onClick={() => handleCountrySelect(country)}
                                                                                                    >
                                                                                                        <span className="travel-country-avatar f-11">
                                                                                                            <CountryAvatarContent country={country} />
                                                                                                            {active && (
                                                                                                                <span className="travel-country-selected-icon">
                                                                                                                    <CheckIcon />
                                                                                                                </span>
                                                                                                            )}
                                                                                                        </span>
                                                                                                        <span className="f-13">{getCountryLabel(country)}</span>
                                                                                                    </button>
                                                                                                </li>
                                                                                            );
                                                                                        })}
                                                                                    </ul>
                                                                                </div>
                                                                            </div>
                                                                        )}
                                                                        {isTripDetailsSection ? (
                                                                            <>
                                                                                {renderSectionHeading("Coverage Detail", true)}
                                                                                <DynamicFormField
                                                                                    fields={blockItem?.fields}
                                                                                    blocks={activeSection?.blocks}
                                                                                    fetchCustomerData={() => { }}
                                                                                    setLoading={setLoading}
                                                                                    onSelectFieldClick={handleSelectFieldClick}
                                                                                    onReadOnlyFieldClick={handleReadOnlyFieldClick}
                                                                                    getSelectMenuOpen={getSelectMenuOpen}
                                                                                    onValueChange={handleTravelFieldChange}
                                                                                    shouldRenderField={isTravelCoverageDetailField}
                                                                                />
                                                                                {renderSectionHeading(tripDetailsTitle, true)}
                                                                                <DynamicFormField
                                                                                    fields={blockItem?.fields}
                                                                                    blocks={activeSection?.blocks}
                                                                                    fetchCustomerData={() => { }}
                                                                                    setLoading={setLoading}
                                                                                    onSelectFieldClick={handleSelectFieldClick}
                                                                                    onReadOnlyFieldClick={handleReadOnlyFieldClick}
                                                                                    getSelectMenuOpen={getSelectMenuOpen}
                                                                                    onValueChange={handleTravelFieldChange}
                                                                                    shouldRenderField={(fieldItem: any) => !isTravelCoverageDetailField(fieldItem)}
                                                                                />
                                                                            </>
                                                                        ) : (
                                                                            <DynamicFormField
                                                                                fields={blockItem?.fields}
                                                                                blocks={activeSection?.blocks}
                                                                                fetchCustomerData={() => { }}
                                                                                setLoading={setLoading}
                                                                                onSelectFieldClick={handleSelectFieldClick}
                                                                                onReadOnlyFieldClick={handleReadOnlyFieldClick}
                                                                                getSelectMenuOpen={getSelectMenuOpen}
                                                                                onValueChange={handleTravelFieldChange}
                                                                            />
                                                                        )}
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="btn-continue btn-bottom-fixed">
                                                <button
                                                    className="btn-primary"
                                                    onClick={handleContinue}
                                                    disabled={loading || (activeSection?.key === "plan_selection" && selectedTravelPlans.length !== 1)}
                                                >
                                                    {activeSection?.btn?.title || "Continue"}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <TravellersDobPopup
                isOpen={isTravellersDobPopupOpen}
                onClose={() => setIsTravellersDobPopupOpen(false)}
                maxTravellerCount={maxTravellerCount}
                travellersCount={travellersCount}
                travellerDobValues={travellerDobValues}
                onSave={handleTravellerDobSave}
            />
            <DestinationCountryPopup
                isOpen={isDestinationPopupOpen}
                onClose={() => setIsDestinationPopupOpen(false)}
                selectedCountries={selectedCountries}
                onSave={handleDestinationPopupSave}
            />
        </>
    );
}
